import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Stack, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getShoulderDystociaRowValues } from "@/encounters/components/EncounterLabor/utils/labor";
import { formatInitiatedTimestamp } from "@/utils/timeUtils";

interface ShoulderDystociaRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  flowsheetId?: string;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function ShoulderDystociaRow({
  time,
  observation,
  showNumbersOnly,
  flowsheetId,
  showPreviousVersions = true,
  isViewOnly = false,
}: ShoulderDystociaRowProps) {
  const { duration, maneuvers, birth, birthNotes, hasAnswers, hasNotes, notes } = useMemo(
    () => getShoulderDystociaRowValues(observation),
    [observation],
  );

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Shoulder Dystocia:</TextWrapper>
            <List fz="sm" withPadding>
              {duration && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Duration:
                    </TextWrapper>
                    <TextWrapper size="sm">{duration}</TextWrapper>
                  </Group>
                </List.Item>
              )}
              <List.Item>
                <TextWrapper size="sm" variant="bold">
                  Maneuvers:
                </TextWrapper>
                <List fz="sm" withPadding listStyleType="decimal">
                  {maneuvers?.map((maneuver, index) => {
                    const initiatedDisplay = formatInitiatedTimestamp(maneuver.initiated);
                    return (
                      <List.Item key={`maneuver-${index}`}>
                        <Stack gap={2}>
                          <Group gap="xs">
                            <TextWrapper size="sm" variant="bold">
                              Type -
                            </TextWrapper>
                            <TextWrapper size="sm">{maneuver.maneuver}</TextWrapper>
                          </Group>
                          {initiatedDisplay && (
                            <TextWrapper size="sm" c="dimmed">
                              {initiatedDisplay}
                            </TextWrapper>
                          )}
                        </Stack>
                      </List.Item>
                    );
                  })}
                </List>
              </List.Item>
              {birth && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Birth Time:
                    </TextWrapper>
                    <TextWrapper size="sm">{birth}</TextWrapper>
                  </Group>
                </List.Item>
              )}
              {birthNotes && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Birth Notes:
                    </TextWrapper>
                    <TextWrapper size="sm">{birthNotes}</TextWrapper>
                  </Group>
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Shoulder Dystocia Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={ShoulderDystociaRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
